import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import type { AdminMatchListItem, AdminMatchUpdatePayload } from '../../services/matchService'
import { LeagueEnum } from '../../types/room'

const LEAGUE_OPTIONS = Object.values(LeagueEnum)

interface ManageMatchModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  match: AdminMatchListItem | null
  onSaved: (updated: AdminMatchListItem) => void
  onSave: (id: string, data: AdminMatchUpdatePayload) => Promise<AdminMatchListItem>
}

export function ManageMatchModal({
  open,
  onOpenChange,
  match,
  onSaved,
  onSave,
}: ManageMatchModalProps) {
  const [loading, setLoading] = useState(false)
  const [teamA, setTeamA] = useState('')
  const [teamB, setTeamB] = useState('')
  const [league, setLeague] = useState('')
  const [score, setScore] = useState('')
  const [winner, setWinner] = useState<string | null>(null)
  const [playoff, setPlayoff] = useState(false)
  const [playoffUniqueGame, setPlayoffUniqueGame] = useState(false)
  const [firstLegMatchScore, setFirstLegMatchScore] = useState<string | null>(null)
  const [winnerAdditionalTime, setWinnerAdditionalTime] = useState<string | null>(null)
  const [winnerPenalties, setWinnerPenalties] = useState<string | null>(null)

  useEffect(() => {
    if (open && match) {
      setTeamA(match.teamA ?? '')
      setTeamB(match.teamB ?? '')
      setLeague(match.league ?? '')
      setScore(match.score ?? '')
      setWinner(match.winner ?? null)
      setPlayoff(match.playoff ?? false)
      setPlayoffUniqueGame(match.playoffUniqueGame ?? false)
      setFirstLegMatchScore(match.firstLegMatchScore ?? null)
      setWinnerAdditionalTime(match.winnerAdditionalTime ?? null)
      setWinnerPenalties(match.winnerPenalties ?? null)
    }
  }, [open, match])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!match?.id) return
    setLoading(true)
    try {
      const data: AdminMatchUpdatePayload = {
        teamA,
        teamB,
        league,
        score: score || undefined,
        winner: winner || undefined,
        playoff,
        playoffUniqueGame,
        firstLegMatchScore: firstLegMatchScore || undefined,
        winnerAdditionalTime: winnerAdditionalTime || undefined,
        winnerPenalties: winnerPenalties || undefined,
      }
      const updated = await onSave(match.id, data)
      onSaved(updated)
      onOpenChange(false)
      toast.success('Match updated')
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Update failed')
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => onOpenChange(false)}>
      <div className="w-full max-w-md glass-strong rounded-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-display font-bold text-foreground">Update match result</h2>
          <button type="button" onClick={() => onOpenChange(false)} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground">×</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Team A</label>
            <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground" value={teamA} onChange={(e) => setTeamA(e.target.value)} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Team B</label>
            <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground" value={teamB} onChange={(e) => setTeamB(e.target.value)} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">League</label>
            <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground" value={league} onChange={(e) => setLeague(e.target.value)}>
              {LEAGUE_OPTIONS.map((l) => (
                <option key={l} value={l}>{l.replace(/-/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Score (90 min)</label>
            <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground" value={score} onChange={(e) => setScore(e.target.value)} placeholder="e.g. 2-1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Winner</label>
            <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground" value={winner ?? ''} onChange={(e) => setWinner(e.target.value || null)}>
              <option value="">Draw</option>
              {teamA && <option value={teamA}>{teamA}</option>}
              {teamB && <option value={teamB}>{teamB}</option>}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">First leg score</label>
            <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground" value={firstLegMatchScore ?? ''} onChange={(e) => setFirstLegMatchScore(e.target.value.trim() || null)} placeholder="e.g. 1-2" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="playoff" checked={playoff} onChange={(e) => setPlayoff(e.target.checked)} />
            <label htmlFor="playoff" className="text-sm text-foreground">Playoff match (two-legged)</label>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="playoffUniqueGame" checked={playoffUniqueGame} onChange={(e) => setPlayoffUniqueGame(e.target.checked)} />
            <label htmlFor="playoffUniqueGame" className="text-sm text-foreground">Single-game playoff (draw → ET/penalties)</label>
          </div>
          {(playoff || playoffUniqueGame) && (
            <>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Winner in additional time</label>
                <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground" value={winnerAdditionalTime ?? ''} onChange={(e) => setWinnerAdditionalTime(e.target.value || null)}>
                  <option value="">—</option>
                  {teamA && <option value={teamA}>{teamA}</option>}
                  {teamB && <option value={teamB}>{teamB}</option>}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Winner on penalties</label>
                <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground" value={winnerPenalties ?? ''} onChange={(e) => setWinnerPenalties(e.target.value || null)}>
                  <option value="">—</option>
                  {teamA && <option value={teamA}>{teamA}</option>}
                  {teamB && <option value={teamB}>{teamB}</option>}
                </select>
              </div>
            </>
          )}
          <div className="flex gap-2 pt-2">
            <button type="button" className="px-4 py-2 rounded-lg border border-border hover:bg-muted/50" onClick={() => onOpenChange(false)}>Cancel</button>
            <button type="submit" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50" disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}
